import type React from "react";
import { useState } from "react";
import { Play } from "lucide-react";
import { PhoneFrame } from "./PhoneFrame";
import { getVideoMeta } from "@/data/videos";

interface PhoneVideo {
  id: string;
  name: string;
  description?: string;
  title: string;
  location: string;
}

interface PhoneShortsSectionProps {
  title?: string;
  highlightedTitle?: string;
  subtitle?: React.ReactNode;
  videos: PhoneVideo[];
}

const THUMB_FORMATS = ["maxresdefault.jpg", "hq720.jpg", "sddefault.jpg", "hqdefault.jpg"];

function PhoneShort({ video }: { video: PhoneVideo }) {
  const [loaded, setLoaded] = useState(false);
  const [thumbIdx, setThumbIdx] = useState(0);
  const thumbnailUrl = `https://img.youtube.com/vi/${video.id}/${THUMB_FORMATS[thumbIdx]}`;
  const alt = getVideoMeta(video.id).name;

  return (
    <div className="flex flex-col items-center">
      <PhoneFrame>
        {loaded ? (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
            title={alt}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            className="group absolute inset-0 h-full w-full"
            aria-label={`Play ${alt}`}
          >
            <img
              src={thumbnailUrl}
              alt={alt}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={() => {
                if (thumbIdx < THUMB_FORMATS.length - 1) setThumbIdx((i) => i + 1);
              }}
            />
            <div className="absolute inset-0 bg-background/30 transition-colors duration-300 group-hover:bg-background/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary shadow-gold transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-1 h-6 w-6 fill-secondary-foreground text-secondary-foreground" />
              </div>
            </div>
          </button>
        )}
      </PhoneFrame>

      <div className="mt-5 max-w-[260px] text-center">
        <p className="text-sm font-semibold leading-snug text-foreground">{video.title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{video.location}</p>
      </div>
    </div>
  );
}

export function PhoneShortsSection({
  title = "See Our",
  highlightedTitle = "Work in Action",
  subtitle = "Real project walkthroughs from across North Jersey",
  videos,
}: PhoneShortsSectionProps) {
  return (
    <section className="bg-card py-20">
      <div className="container mx-auto px-4">
        <h2 className="mb-3 text-center text-3xl font-bold md:text-4xl">
          {title} <span className="text-gradient-gold">{highlightedTitle}</span>
        </h2>
        {subtitle && (
          <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">{subtitle}</p>
        )}

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-8 lg:grid-cols-4 lg:gap-6">
          {videos.map((v) => (
            <PhoneShort key={v.id} video={v} />
          ))}
        </div>
      </div>
    </section>
  );
}
